import { useState } from "react";
import { getAuth, sendPasswordResetEmail, fetchSignInMethodsForEmail } from "firebase/auth";
import { parentsFirebase } from "app/Auth";
import { usePlaySound } from "common/utils";
import parentI18n from "features/parent/i18n";
import { MobileInput } from "./AuthStyles";

export const PasswordResetForm = ({
  onBackToLogin,
  onLoadingChange,
  showBackBeforeSent = false,
}: {
  onBackToLogin: () => void;
  onLoadingChange?: (loading: boolean) => void;
  showBackBeforeSent?: boolean;
}) => {
  const playSound = usePlaySound();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);

  const setLoading = (loading: boolean) => {
    setIsLoading(loading);
    onLoadingChange?.(loading);
  };

  const handlePasswordReset = async () => {
    playSound();
    if (!email) {
      setError(parentI18n.t("MSG_SIGN_EMAIL_REQUIRED"));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(parentI18n.t("MSG_SIGN_EMAIL_INVALID"));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const auth = getAuth(parentsFirebase);
      const methods = await fetchSignInMethodsForEmail(auth, email);
      if (methods.includes("password")) {
        await sendPasswordResetEmail(auth, email);
      }
      setIsEmailSent(true);
    } catch {
      setError(parentI18n.t("MSG_SIGN_PASSWORD_RESET_FAILED"));
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    playSound();
    onBackToLogin();
  };

  if (isEmailSent) {
    return (
      <div className="flex-col-el flex-center gap-4 w-full">
        <div className="flex-row-el flex-center gap-2">
          <span className="material-symbols-outlined type-title text-green-100">check_circle</span>
          <p className="type-text type-body text-textcolor-black">
            {parentI18n.t("MSG_SIGN_EMAIL_SENT")}
          </p>
        </div>

        <p className="type-text type-sm text-textcolor-black break-all text-center">{email}</p>

        <p className="type-text type-sm text-gray2-100 text-center">
          {parentI18n.t("MSG_SIGN_PASSWORD_RESET_SENT")}
        </p>

        <button
          type="button"
          className="w-[375px] py-3 bg-green-100 text-white rounded-xl border-none cursor-pointer hover:opacity-90 transition-all mt-4 type-inherit-font type-text type-sm"
          onClick={handleBackToLogin}
        >
          {parentI18n.t("MSG_SIGN_BACK_TO_LOGIN")}
        </button>
      </div>
    );
  }

  return (
    <div className="flex-col-el flex-center w-full gap-4">
      <MobileInput
        type="email"
        value={email}
        onChange={(e) => {
          setError("");
          setEmail(e.target.value);
        }}
        placeholder={parentI18n.t("MSG_SIGN_USER_MAIL")}
        label={parentI18n.t("MSG_SIGN_USER_MAIL")}
        disabled={isLoading}
      />

      {error && <p className="type-text type-sm text-danger text-center">{error}</p>}

      <p className="type-text type-sm text-center text-gray2-100">
        {parentI18n.t("MSG_SIGN_PASSWORD_RESET_DESCRIPTION")}
      </p>

      <button
        type="button"
        className="w-[375px] py-3 bg-green-100 text-white rounded-xl border-none cursor-pointer hover:opacity-90 transition-all disabled:opacity-50 type-inherit-font type-text type-sm"
        onClick={handlePasswordReset}
        disabled={isLoading}
      >
        {parentI18n.t("MSG_SIGN_PASSWORD_RESET_SEND_BTN")}
      </button>

      {showBackBeforeSent && (
        <button
          type="button"
          onClick={handleBackToLogin}
          className="bg-transparent border-none text-textcolor-black/70 hover:text-textcolor-black cursor-pointer type-inherit-font type-text type-sm flex-row-el items-center gap-1"
        >
          <span className="material-symbols-outlined type-md">arrow_back</span>
          {parentI18n.t("MSG_BACK_BTN")}
        </button>
      )}
    </div>
  );
};
